/**
 * Simplified EXIF Orientation Utils
 * Takes the image exactly as it appears on screen (browser-corrected) and puts it on Canvas
 */
class ImageExifUtils {
  
  /**
   * Enhanced EXIF orientation reading with better marker detection
   * @param {File} file - Image file to read
   * @returns {Promise<number>} - EXIF orientation value (1-8)
   */
  static async getExifOrientation(file) {
    return new Promise((resolve) => {
      try {
        const reader = new FileReader();
        reader.onload = (e) => {
          const view = new DataView(e.target.result);
          
          // Check if it's a JPEG file
          if (view.getUint16(0, false) !== 0xFFD8) {
            console.log('[ImageExifUtils] Not a JPEG file, defaulting to orientation 1');
            resolve(1);
            return;
          }
          
          console.log('[ImageExifUtils] Starting EXIF scan for file:', file.name);
          console.log('[ImageExifUtils] File size:', file.size, 'bytes');
          console.log('[ImageExifUtils] JPEG SOI marker found: 0xFFD8');
          
          let offset = 2;
          let marker;
          let foundExif = false;
          
          // Find EXIF marker - scan through all segments
          while (offset < view.byteLength - 1) {
            try {
              marker = view.getUint16(offset, false);
              console.log(`[ImageExifUtils] Found marker: 0x${marker.toString(16).toUpperCase()} at offset ${offset}`);
              offset += 2;
              
              // Check for APP1 marker (0xFFE1) - most common for EXIF
              if (marker === 0xFFE1) {
                const segmentLength = view.getUint16(offset, false);
                console.log(`[ImageExifUtils] APP1 segment length: ${segmentLength}`);
                
                if (segmentLength >= 14 && offset + segmentLength <= view.byteLength) {
                  // Check for EXIF identifier
                  const exifIdentifier = new Uint8Array(view.buffer, offset + 2, 6);
                  const exifString = String.fromCharCode.apply(null, exifIdentifier);
                  console.log(`[ImageExifUtils] APP1 identifier: "${exifString}" (bytes: ${Array.from(exifIdentifier).map(b => '0x' + b.toString(16)).join(', ')})`);
                  
                  if (exifString === 'Exif\0\0') {
                    console.log('[ImageExifUtils] Found EXIF segment in APP1');
                    foundExif = true;
                    const orientation = this.readExifOrientation(view, offset + 8);
                    console.log('[ImageExifUtils] EXIF orientation detected:', orientation);
                    resolve(orientation);
                    return;
                  }
                }
                
                offset += segmentLength;
              }
              // Check for other APP markers (0xFFE0 to 0xFFEF)
              else if (marker >= 0xFFE0 && marker <= 0xFFEF) {
                const segmentLength = view.getUint16(offset, false);
                console.log(`[ImageExifUtils] APP${marker & 0x0F} segment length: ${segmentLength}`);
                
                if (segmentLength >= 14 && offset + segmentLength <= view.byteLength) {
                  // Check for EXIF identifier in any APP segment
                  const identifierLength = Math.min(10, segmentLength - 2);
                  const identifier = new Uint8Array(view.buffer, offset + 2, identifierLength);
                  const identifierString = String.fromCharCode.apply(null, identifier);
                  console.log(`[ImageExifUtils] APP${marker & 0x0F} identifier: "${identifierString}"`);
                  
                  // Look for EXIF in various positions
                  for (let i = 0; i <= identifierLength - 6; i++) {
                    const testId = String.fromCharCode.apply(null, identifier.slice(i, i + 6));
                    if (testId === 'Exif\0\0') {
                      console.log(`[ImageExifUtils] Found EXIF identifier at position ${i} in APP${marker & 0x0F}`);
                      foundExif = true;
                      const orientation = this.readExifOrientation(view, offset + 2 + i + 6);
                      console.log('[ImageExifUtils] EXIF orientation detected:', orientation);
                      resolve(orientation);
                      return;
                    }
                  }
                }
                
                offset += segmentLength;
              } else if (marker === 0xFFDA) {
                // Start of image data, no more markers
                console.log('[ImageExifUtils] Reached start of image data (SOS)');
                break;
              } else if (marker === 0xFFD9) {
                // End of image
                console.log('[ImageExifUtils] Reached end of image (EOI)');
                break;
              } else if ((marker & 0xFF00) === 0xFF00) {
                // Other JPEG markers
                if (offset >= view.byteLength - 1) break;
                const length = view.getUint16(offset, false);
                console.log(`[ImageExifUtils] Skipping marker 0x${marker.toString(16)}, length: ${length}`);
                offset += length;
              } else {
                // Invalid marker or corrupted data
                console.warn(`[ImageExifUtils] Invalid marker or corrupted data at offset ${offset - 2}: 0x${marker.toString(16)}`);
                break;
              }
              
              // Safety check to prevent infinite loops
              if (offset >= view.byteLength - 4) {
                console.log('[ImageExifUtils] Reached end of searchable data');
                break;
              }
              
            } catch (error) {
              console.warn(`[ImageExifUtils] Error reading marker at offset ${offset}:`, error);
              break;
            }
          }
          
          if (!foundExif) {
            console.log('[ImageExifUtils] No EXIF data found in JPEG after complete scan');
          }
          resolve(1);
        };
        
        reader.onerror = () => {
          console.warn('[ImageExifUtils] Error reading file for EXIF, defaulting to orientation 1');
          resolve(1);
        };
        
        // Read more data to ensure we capture EXIF (iPhones can have large EXIF sections)
        reader.readAsArrayBuffer(file.slice(0, 128 * 1024)); // Read first 128KB
      } catch (error) {
        console.warn('[ImageExifUtils] Error in getExifOrientation:', error);
        resolve(1);
      }
    });
  }
  
  /**
   * Enhanced EXIF orientation reading with better endianness handling
   * @param {DataView} view - DataView of EXIF data
   * @param {number} offset - Offset to start reading
   * @returns {number} - Orientation value (1-8)
   */
  static readExifOrientation(view, offset) {
    try {
      console.log('[ImageExifUtils] Reading EXIF orientation from offset:', offset);
      
      if (offset + 8 >= view.byteLength) {
        console.warn('[ImageExifUtils] Not enough data to read TIFF header');
        return 1;
      }
      
      // Check TIFF header byte order
      const byteOrder1 = view.getUint16(offset, false);
      const byteOrder2 = view.getUint16(offset, true);
      
      console.log(`[ImageExifUtils] Byte order markers: 0x${byteOrder1.toString(16)} (big), 0x${byteOrder2.toString(16)} (little)`);
      
      let littleEndian = false;
      if (byteOrder1 === 0x4949) { // "II" - Intel (little endian)
        littleEndian = true;
        console.log('[ImageExifUtils] Using little endian');
      } else if (byteOrder1 === 0x4D4D) { // "MM" - Motorola (big endian)
        littleEndian = false;
        console.log('[ImageExifUtils] Using big endian');
      } else {
        console.warn('[ImageExifUtils] Invalid TIFF header, trying both endianness');
        // Try both and see which gives valid magic number
        const magic1 = view.getUint16(offset + 2, false);
        const magic2 = view.getUint16(offset + 2, true);
        console.log(`[ImageExifUtils] Magic numbers: ${magic1} (big), ${magic2} (little)`);
        
        if (magic1 === 42) {
          littleEndian = false;
        } else if (magic2 === 42) {
          littleEndian = true;
        } else {
          console.warn('[ImageExifUtils] Could not determine endianness');
          return 1;
        }
      }
      
      // Check TIFF magic number (should be 42)
      const magic = view.getUint16(offset + 2, littleEndian);
      console.log('[ImageExifUtils] TIFF magic number:', magic);
      
      if (magic !== 42) {
        console.warn('[ImageExifUtils] Invalid TIFF magic number:', magic);
        return 1;
      }
      
      // Get first IFD offset
      let ifdOffset = view.getUint32(offset + 4, littleEndian);
      console.log('[ImageExifUtils] IFD offset:', ifdOffset);
      ifdOffset += offset;
      
      if (ifdOffset + 2 >= view.byteLength) {
        console.warn('[ImageExifUtils] IFD offset out of bounds');
        return 1;
      }
      
      // Read number of entries
      const entries = view.getUint16(ifdOffset, littleEndian);
      console.log('[ImageExifUtils] Number of IFD entries:', entries);
      
      // Validate entries count
      if (entries > 200 || entries === 0) {
        console.warn('[ImageExifUtils] Suspicious number of IFD entries:', entries);
        return 1;
      }
      
      ifdOffset += 2;
      
      // Look for orientation tag (0x0112)
      for (let i = 0; i < entries; i++) {
        if (ifdOffset + 12 > view.byteLength) {
          console.warn('[ImageExifUtils] Entry offset out of bounds');
          break;
        }
        
        const tag = view.getUint16(ifdOffset, littleEndian);
        const type = view.getUint16(ifdOffset + 2, littleEndian);
        const count = view.getUint32(ifdOffset + 4, littleEndian);
        
        console.log(`[ImageExifUtils] Entry ${i}: Tag: 0x${tag.toString(16)}, Type: ${type}, Count: ${count}`);
        
        if (tag === 0x0112) {
          // Found orientation tag
          let orientation;
          if (type === 3) { // SHORT
            orientation = view.getUint16(ifdOffset + 8, littleEndian);
          } else if (type === 4) { // LONG
            orientation = view.getUint32(ifdOffset + 8, littleEndian);
          } else {
            // Try reading as SHORT anyway
            orientation = view.getUint16(ifdOffset + 8, littleEndian);
          }
          
          console.log('[ImageExifUtils] Found orientation tag with value:', orientation, 'type:', type);
          
          // Validate orientation value
          if (orientation >= 1 && orientation <= 8) {
            return orientation;
          } else {
            console.warn('[ImageExifUtils] Invalid orientation value:', orientation);
            return 1;
          }
        }
        
        ifdOffset += 12;
      }
      
      console.log('[ImageExifUtils] Orientation tag not found in IFD');
      return 1;
    } catch (error) {
      console.warn('[ImageExifUtils] Error reading EXIF orientation:', error);
      return 1;
    }
  }
  
  /**
   * SIMPLIFIED: Capture image exactly as it appears on screen and put it on Canvas
   * @param {File} file - Original image file
   * @param {number} orientation - EXIF orientation value (for logging only)
   * @param {number} quality - Output quality (0.1 to 1.0)
   * @returns {Promise<File>} - Canvas file with the visual appearance
   */
  static async applyExifOrientationToCanvas(file, orientation, quality = 0.95) {
    return new Promise((resolve, reject) => {
      try {
        console.log('[ImageExifUtils] ===== ABORDAGEM VISUAL SIMPLES =====');
        console.log('[ImageExifUtils] Capturando imagem como aparece na tela');
        console.log('[ImageExifUtils] EXIF orientation:', orientation, '(apenas para log)');
        
        // Create image to get the visual appearance (browser-corrected)
        const img = new Image();
        img.onload = () => {
          try {
            console.log('[ImageExifUtils] ===== CAPTURA VISUAL =====');
            console.log('[ImageExifUtils] Dimensões visuais (já corrigidas pelo browser):', {
              naturalWidth: img.naturalWidth,
              naturalHeight: img.naturalHeight,
              exifOrientation: orientation
            });
            
            // Create canvas with the visual dimensions (what we see on screen)
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
              reject(new Error('Could not get canvas context'));
              return;
            }
            
            console.log('[ImageExifUtils] ===== CANVAS DIRETO =====');
            console.log('[ImageExifUtils] Canvas configurado:', {
              canvasWidth: canvas.width,
              canvasHeight: canvas.height,
              method: 'visual_direct_copy'
            });
            
            // Draw the image EXACTLY as it appears visually - NO TRANSFORMATIONS
            console.log('[ImageExifUtils] Desenhando imagem sem nenhuma transformação');
            ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);
            
            // Validate that we have image data
            const centerPixel = ctx.getImageData(Math.floor(canvas.width/2), Math.floor(canvas.height/2), 1, 1).data;
            const hasImageData = centerPixel[3] > 0; // Check alpha channel
            
            console.log('[ImageExifUtils] Validação do Canvas:', {
              hasImageData: hasImageData,
              centerPixel: `rgba(${centerPixel[0]}, ${centerPixel[1]}, ${centerPixel[2]}, ${centerPixel[3]})`,
              canvasEmpty: !hasImageData
            });
            
            if (!hasImageData) {
              console.warn('[ImageExifUtils] WARNING: Canvas parece estar vazio!');
            }
            
            // Convert to blob
            console.log('[ImageExifUtils] ===== CONVERSÃO PARA BLOB =====');
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  console.log('[ImageExifUtils] ===== CAPTURA VISUAL CONCLUÍDA =====');
                  console.log('[ImageExifUtils] Captura visual bem-sucedida:', {
                    originalName: file.name,
                    originalSize: file.size,
                    canvasSize: blob.size,
                    exifOrientation: orientation,
                    method: 'visual_direct_capture',
                    transformations: 'none'
                  });
                  
                  const resultFile = new File([blob], file.name, {
                    type: file.type,
                    lastModified: Date.now()
                  });
                  resolve(resultFile);
                } else {
                  reject(new Error('Failed to create blob from visual canvas'));
                }
              },
              'image/jpeg',
              quality
            );
            
          } catch (error) {
            console.error('[ImageExifUtils] Erro na captura visual:', error);
            reject(error);
          }
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image for visual capture'));
        };
        
        const reader = new FileReader();
        reader.onload = (e) => {
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
        
      } catch (error) {
        console.error('[ImageExifUtils] Erro na configuração de captura visual:', error);
        reject(error);
      }
    });
  }
  
  /**
   * SIMPLIFIED: Process image orientation using visual capture approach
   * @param {File} file - Image file to process
   * @param {number} quality - Output quality (not used much)
   * @returns {Promise<Object>} - Object with original file and visually captured file for upload
   */
  static async processImageOrientation(file, quality = 0.95) {
    try {
      console.log('[ImageExifUtils] ===== PROCESSAMENTO VISUAL SIMPLIFICADO =====');
      console.log('[ImageExifUtils] Processando imagem:', file.name);
      
      // Check if this file has already been processed to avoid double processing
      if (file._exifProcessed) {
        console.log('[ImageExifUtils] ANTI-DUPLO-PROCESSAMENTO: Arquivo já processado, retornando como está');
        return {
          originalFile: file,
          uploadFile: file
        };
      }
      
      console.log('[ImageExifUtils] NOVA ABORDAGEM: Captura visual direta');
      
      // Get EXIF orientation (for logging only)
      const exifOrientation = await this.getExifOrientation(file);
      console.log('[ImageExifUtils] Orientação EXIF detectada:', exifOrientation, '(apenas informativo)');
      
      // Apply visual capture - takes image as browser shows it
      const uploadFile = await this.applyExifOrientationToCanvas(file, exifOrientation, quality);
      
      // Mark the original file as processed
      const processedOriginalFile = new File([file], file.name, {
        type: file.type,
        lastModified: file.lastModified
      });
      processedOriginalFile._exifProcessed = true;
      
      console.log('[ImageExifUtils] ===== PROCESSAMENTO VISUAL CONCLUÍDO =====');
      console.log('[ImageExifUtils] Processamento visual de imagem concluído:', {
        originalName: file.name,
        originalSize: file.size,
        uploadSize: uploadFile.size,
        exifOrientation: exifOrientation,
        method: 'visual_direct_capture',
        doubleProcessingProtected: true
      });
      
      // Return both files: original for display, upload file for server
      return {
        originalFile: processedOriginalFile, // For display (browser handles EXIF)
        uploadFile: uploadFile // For upload (visual capture - no double rotation)
      };
      
    } catch (error) {
      console.error('[ImageExifUtils] Erro no processamento visual:', error);
      return {
        originalFile: file,
        uploadFile: file
      }; // Return original files as fallback
    }
  }
  
  /**
   * LEGACY COMPATIBILITY: Returns original file unchanged
   * @param {File} file - Original image file
   * @param {number} orientation - EXIF orientation value (for logging only)
   * @param {number} quality - Output quality (not used)
   * @returns {Promise<File>} - Returns original file unchanged
   */
  static async correctImageOrientation(file, orientation, quality = 0.98) {
    console.log('[ImageExifUtils] LEGACY: correctImageOrientation called - delegating to processImageOrientation');
    const result = await this.processImageOrientation(file, quality);
    return result.originalFile;
  }
  
  // DEPRECATED methods kept for backward compatibility but simplified
  
  static async detectVisualOrientation(file) {
    console.log('[ImageExifUtils] DEPRECATED: detectVisualOrientation - always returns 1');
    return 1;
  }
  
  static async forceOrientationCorrection(file, degrees = 90, quality = 0.98) {
    console.log('[ImageExifUtils] DEPRECATED: forceOrientationCorrection - delegating to processImageOrientation');
    const result = await this.processImageOrientation(file, quality);
    return result.uploadFile;
  }
  
  static detectBrowserRotation(img, orientation) {
    console.log('[ImageExifUtils] DEPRECATED: detectBrowserRotation - not needed with visual approach');
    return 'NONE';
  }
  
  static applyCanvasTransform(ctx, orientation, width, height) {
    console.log('[ImageExifUtils] DEPRECATED: applyCanvasTransform - not used in visual approach');
    // No transformation applied
  }
  
  static getCanvasDimensions(width, height, orientation) {
    console.log('[ImageExifUtils] DEPRECATED: getCanvasDimensions - returning original dimensions');
    return { width, height };
  }
}

// Export the class
window.ImageExifUtils = ImageExifUtils;
